import React, { useState } from "react";
import { motion } from "framer-motion";
import { Lock, ArrowRight } from "lucide-react";
import api, { formatApiErrorDetail } from "../services/api";

export default function ComingSoon({ onUnlock }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showCode, setShowCode] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    setErr("");
    try {
      await api.post("/access/verify", { code: code.trim() });
      localStorage.setItem("pq_site_access", "granted");
      onUnlock();
    } catch (ex) {
      setErr(formatApiErrorDetail(ex.response?.data?.detail) || "That code isn't right.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-6 text-center relative overflow-hidden no-select"
      style={{ background: "linear-gradient(165deg,#1E1B4B 0%,#4C1D95 55%,#7C3AED 100%)" }}
      data-testid="coming-soon"
    >
      {/* floating letters */}
      {["A", "b", "C", "s", "t", "P", "i", "n", "m", "o"].map((l, i) => (
        <motion.span
          key={i}
          className="absolute font-display font-black text-white/10 select-none"
          style={{ left: `${(i * 37) % 95}%`, top: `${(i * 53) % 90}%`, fontSize: `${2 + (i % 4)}rem` }}
          animate={{ y: [0, -18, 0], rotate: [-8, 8, -8] }}
          transition={{ repeat: Infinity, duration: 5 + (i % 4), delay: i * 0.3 }}
        >
          {l}
        </motion.span>
      ))}

      <motion.div
        initial={{ scale: 0.8, opacity: 0, y: 24 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 200, damping: 16 }}
        className="relative z-10 w-full max-w-lg"
      >
        <motion.div
          animate={{ y: [0, -12, 0], rotate: [-4, 4, -4] }}
          transition={{ repeat: Infinity, duration: 3 }}
          className="text-8xl mb-4 drop-shadow-[0_8px_20px_rgba(0,0,0,0.3)]"
        >
          🦉
        </motion.div>

        <h1 className="font-display text-5xl sm:text-6xl font-black text-white tracking-tight mb-2">Syllaberry</h1>
        <p className="font-display text-xl font-bold text-amber-300 mb-4">Reading adventures are coming soon! ✨</p>
        <p className="text-indigo-100/80 font-semibold text-base max-w-md mx-auto mb-8 leading-relaxed">
          We're busy building a magical world where little ones learn to read through play.
          Check back soon — big things are hatching! 🐣
        </p>

        {!showCode ? (
          <button
            data-testid="have-code-btn"
            onClick={() => setShowCode(true)}
            className="inline-flex items-center gap-2 text-indigo-200/70 hover:text-white font-bold text-sm transition-colors"
          >
            <Lock className="w-4 h-4" /> I have an access code
          </button>
        ) : (
          <motion.form
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            onSubmit={submit}
            className="max-w-xs mx-auto"
          >
            <div className="flex gap-2">
              <input
                data-testid="access-code-input"
                autoFocus
                value={code}
                onChange={(e) => { setCode(e.target.value); setErr(""); }}
                placeholder="Access code"
                className="flex-1 rounded-2xl border-4 border-white/20 bg-white/10 backdrop-blur text-white placeholder-white/50 px-4 py-3 font-bold outline-none focus:border-amber-300"
              />
              <button
                data-testid="access-code-submit"
                type="submit"
                disabled={busy}
                className="pop-btn rounded-2xl bg-amber-400 text-indigo-900 font-black px-4 flex items-center justify-center shadow-[0_5px_0_#B45309] active:shadow-none disabled:opacity-60"
              >
                {busy ? "…" : <ArrowRight strokeWidth={3} />}
              </button>
            </div>
            {err && <p data-testid="access-code-error" className="text-red-300 font-bold text-sm mt-3">{err}</p>}
          </motion.form>
        )}
      </motion.div>
    </div>
  );
}
